/**
 * What you buy versus what you allocate (`docs/catalog-units-and-buying.md` §4).
 *
 * A paver is bought by the skid and allocated by the square foot; a skid steer
 * is hired by the week and sold by the day; a roll-off is paid by the haul and
 * filled by the cubic yard. One conversion says how many allocation units are
 * in one buying unit, and one rule says what a job pays when it uses part of
 * one: only what it uses (the rest goes back to stock, or sits idle) or the
 * whole thing.
 *
 * This is the one place that arithmetic lives. The quote line's cost, the
 * "loses money" check, the sentence under a line, the package preview and the
 * catalog form's Try it panel all call it, so they cannot disagree about what
 * 1,200 sq ft of paver costs.
 */
import { marginPctOf } from './quote-margin.js';
/** Float noise must not buy a skid for 1,040.0000000001 sq ft when two skids are 1,040. */
const EPSILON = 1e-9;
const cents = (value) => Math.round(value * 100) / 100;
/** Cost of one allocation unit. Stored to 6 dp. */
export const costPerAlloc = (r) => r.buyPrice / r.per;
export function buyPlan(rule, qty) {
    if (!(qty > 0) || !(rule.per > 0)) {
        return { buyUnits: 0, allocBought: 0, leftover: 0, usedCost: 0, wholeCost: 0, cost: 0 };
    }
    const buyUnits = Math.max(Math.ceil(qty / rule.per - EPSILON), rule.minOrder || 0);
    const allocBought = buyUnits * rule.per;
    const leftover = Math.max(0, allocBought - qty);
    const usedCost = qty * rule.buyPrice / rule.per;
    const wholeCost = buyUnits * rule.buyPrice;
    return {
        buyUnits,
        allocBought,
        leftover,
        usedCost: cents(usedCost),
        wholeCost: cents(wholeCost),
        cost: cents(rule.basis === 'whole' ? wholeCost : usedCost),
    };
}
/**
 * What a quote line costs: through its buying rule when it has one, else
 * `qty × unitCost` exactly as every line was costed before the rule existed.
 * Rounded to cents once.
 */
export function lineCost(rule, qty, unitCost) {
    if (rule)
        return buyPlan(rule, qty).cost;
    return cents(qty * unitCost);
}
/**
 * Whether a rule has anything to say. A conversion of 1 costed on what it
 * uses is exactly a plain `qty × unit cost` line, so it is stored as no rule
 * at all and prints nothing.
 */
export const ruleMatters = (r) => r.per !== 1 || r.basis === 'whole';
// ---------------------------------------------------------------------------
// The words
// ---------------------------------------------------------------------------
/** The short forms an allocation unit is printed in after a number. */
const SHORT_UNITS = {
    sf: 'sq ft', sy: 'sq yd', cy: 'cu yd', lf: 'lin ft', ft: 'ft',
    gal: 'gal', qt: 'qt', oz: 'oz', lb: 'lb', hr: 'h',
};
function plural(word, n) {
    if (n === 1)
        return word;
    if (/(s|x|z|ch|sh)$/.test(word))
        return `${word}es`;
    return `${word}s`;
}
/** `360 sq ft`, `2 days`, `1 week`. */
function unitWord(code, name, n) {
    const short = SHORT_UNITS[code.toLowerCase()];
    if (short)
        return short;
    return plural((name || code).toLowerCase(), n);
}
const number = (n) => n.toLocaleString('en-US', { maximumFractionDigits: 3 });
const money = (n) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const article = (word) => (/^[aeiou]/i.test(word) ? 'an' : 'a');
/**
 * The tone of a line bought whole: losing money is `bad`, under the company's
 * target is `warn`. A line costed on what it uses is always `plain` — its
 * leftover goes back to stock and costs the job nothing.
 */
export function buyingTone(rule, plan, amount, targetPct) {
    if (rule.basis !== 'whole' || amount <= 0)
        return 'plain';
    const margin = (amount - plan.cost) / amount * 100;
    if (margin < 0)
        return 'bad';
    if (margin < targetPct)
        return 'warn';
    return 'plain';
}
/**
 * The sentence under a quote line and on the purchase order (Board 2), or
 * `null` when there is nothing to say: a conversion of 1 with nothing left
 * over is a line bought exactly as it is sold.
 */
export function buyingPhrase(rule, plan, kind, amount, targetPct = 36) {
    if (plan.buyUnits <= 0)
        return null;
    if (rule.per === 1 && plan.leftover <= EPSILON)
        return null;
    const qty = plan.allocBought - plan.leftover;
    const n = plan.buyUnits;
    const bought = `${number(n)} ${plural(rule.buyUnitName.toLowerCase(), n)}`;
    const left = plan.leftover > EPSILON
        ? `${number(plan.leftover)} ${unitWord(rule.allocUnit, rule.allocUnitName, plan.leftover)}`
        : null;
    const whole = rule.basis === 'whole';
    const parts = [];
    if (kind === 'item') {
        const alloc = Math.abs(plan.allocBought - qty) > EPSILON
            ? ` (${number(plan.allocBought)} ${unitWord(rule.allocUnit, rule.allocUnitName, plan.allocBought)})`
            : '';
        parts.push(`Buy ${bought}${alloc}`);
        if (left)
            parts.push(`${left} ${whole ? 'left over' : 'back to stock'}`);
    }
    else if (kind === 'equipment') {
        parts.push(`Hired ${bought}`);
        if (left)
            parts.push(`${left} idle`);
    }
    else {
        parts.push(bought);
        if (left)
            parts.push(`${left} empty`);
    }
    let text = parts.join(' · ');
    if (!whole) {
        text += ' · costed only on what it uses';
    }
    else {
        text += ' · paid in full';
        if (kind === 'equipment') {
            text += ` — the job costs ${money(plan.cost)} for ${number(qty)} `
                + `${unitWord(rule.allocUnit, rule.allocUnitName, qty)} sold`;
        }
    }
    return { text, tone: buyingTone(rule, plan, amount, targetPct) };
}
/**
 * Before you send, for a line bought whole that loses money (contract §5.5):
 * `Skid steer, T595 loses money — 3 days sold, a week bought. Price the day
 * higher, or quote a full week.`
 */
export function losesMoneyText(name, rule, plan) {
    const qty = plan.allocBought - plan.leftover;
    const buyOne = rule.buyUnitName.toLowerCase();
    const allocOne = unitWord(rule.allocUnit, rule.allocUnitName, 1);
    const bought = plan.buyUnits === 1
        ? `${article(buyOne)} ${buyOne}`
        : `${number(plan.buyUnits)} ${plural(buyOne, plan.buyUnits)}`;
    return `${name} loses money — ${number(qty)} ${unitWord(rule.allocUnit, rule.allocUnitName, qty)} sold, `
        + `${bought} bought. Price the ${allocOne} higher, or quote a full ${buyOne}.`;
}
// ---------------------------------------------------------------------------
// The catalog forms (Board 1, contract §6.1)
// ---------------------------------------------------------------------------
/** A unit after a number, the way every sentence here prints one: `sq ft`, `days`, `week`. */
export { unitWord as buyingUnitWord };
/** `$486.50`, `$0.9356`: cents always, and the four places a unit cost needs. */
const rate = (n) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
/**
 * What one allocation unit sells for — a typed price, or the cost marked up
 * and rounded to cents. The same rule `SELL_PRICE_SQL` applies to a hired-in
 * item or a fee and `LABOR_BILLED` to a crew hour, so the form's Readout and
 * the list's Sells column cannot disagree.
 */
export function sellsPer(cost, mode, price, markupPct) {
    return mode === 'markup' ? cents(cost * (1 + markupPct / 100)) : price;
}
/**
 * The green sentence under the two pairs (the `Band`):
 * `1 skid costs $486.50 and allocates as 520 sq ft — a job is costed at $0.9356 per sq ft.`
 */
export function buyingSentence(rule) {
    const buyOne = rule.buyUnitName.toLowerCase();
    return `1 ${buyOne} costs ${money(rule.buyPrice)} and allocates as ${number(rule.per)} `
        + `${unitWord(rule.allocUnit, rule.allocUnitName, rule.per)} — a job is costed at `
        + `${rate(costPerAlloc(rule))} per ${unitWord(rule.allocUnit, rule.allocUnitName, 1)}.`;
}
/** Labor's, which reads the other way round on purpose (§6.1): four people to a crew hour. */
export function laborSentence(crewSize, personHourCost, crewUnitName, personUnitName) {
    const crew = crewUnitName.toLowerCase();
    const person = personUnitName.toLowerCase();
    return `1 ${crew} is ${number(crewSize)} ${plural(person, crewSize)} at ${money(personHourCost)} — `
        + `a job is costed at ${money(personHourCost * crewSize)} per ${crew}.`;
}
/**
 * The Try it panel (Board 1): what `qty` of this costs the job, sells for and
 * makes, and what is bought to cover it. The arithmetic is `buyPlan`'s; this
 * only says it.
 */
export function tryIt(rule, qty, sellsEach, kind) {
    const plan = buyPlan(rule, qty);
    const sells = cents(qty * sellsEach);
    const marginPct = marginPctOf(Math.round(sells * 100), Math.round(plan.cost * 100));
    const buyOne = rule.buyUnitName.toLowerCase();
    const allocOne = unitWord(rule.allocUnit, rule.allocUnitName, 1);
    const leftWord = unitWord(rule.allocUnit, rule.allocUnitName, plan.leftover);
    const over = plan.leftover > EPSILON
        ? `${number(plan.leftover)} ${leftWord} ${kind === 'item' ? 'back to stock' : kind === 'equipment' ? 'idle' : 'empty'}`
        : null;
    let note = null;
    if (rule.basis === 'whole' && marginPct !== null) {
        if (marginPct < 0) {
            note = `Loses money on a ${number(qty)}-${allocOne} job. Price the ${allocOne} higher, `
                + `or quote a full ${buyOne}.`;
        }
        else if (marginPct < 20) {
            note = `Thin at ${number(qty)} ${unitWord(rule.allocUnit, rule.allocUnitName, qty)}. `
                + `It only pays at about ${number(plan.allocBought)}.`;
        }
    }
    return {
        plan, costs: plan.cost, sells, marginPct,
        bought: `${number(plan.buyUnits)} ${plural(buyOne, plan.buyUnits)}`,
        over, note,
    };
}
// ---------------------------------------------------------------------------
// The order and the stock (Board 2's right-hand column, contract §8)
// ---------------------------------------------------------------------------
/**
 * An allocation quantity said in the unit it was bought in — the muted half of
 * the stock card's `360 sq ft · 0.69 skid` and `1,560 sq ft · 3.00 skids`.
 * Null where they are one unit: there is nothing to translate.
 */
export function buyingStock(qty, rule) {
    if (!(rule.per > 0) || rule.per === 1)
        return null;
    const n = qty / rule.per;
    const one = rule.buyUnitName.toLowerCase();
    return `${n.toFixed(2)} ${n > 1 ? plural(one, 2) : one}`;
}
/** How many of the vendor's unit: `3 skids`, `1 pallet`. */
export function buyingCount(buyQty, buyUnitName) {
    return `${number(buyQty)} ${plural(buyUnitName.toLowerCase(), buyQty)}`;
}
/** A PO line in the vendor's unit: `3 skids × $486.50 = 1,560 sq ft`. */
export function poLineWords(buyQty, buyUnitName, buyPrice, allocQty, allocUnit) {
    return `${buyingCount(buyQty, buyUnitName)} × ${money(buyPrice)} = `
        + `${number(allocQty)} ${unitWord(allocUnit, undefined, allocQty)}`;
}
//# sourceMappingURL=buying.js.map